import { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { UpsellingPackageCard } from 'components/UpsellModal/UpsellingPackageCard';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import type { ISelection } from 'old-store/types/gallery';
import { toggleUpsellModal } from 'store/slices/modals';
import {
  selectActiveLikedImages,
  selectActiveSelectionState
} from 'store/selectors/activeSelection';
import { UpsellingAllImagesCard } from 'components/UpsellModal/UpsellingAllImagesCard';
import { HiSparkles } from 'react-icons/hi';
import { TbEdit } from 'react-icons/tb';
import { FiShoppingBag } from 'react-icons/fi';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { SetIcon } from 'components/icons/SetIcon';
import type { IIconProps } from 'types/icons';
import { getTranslationKey } from 'helpers/texting';
import { PriceBreakdown } from './PriceBreakdown';
import { getEndCustomer } from 'old-store/actions/endCustomer';
import { useCurrency } from 'hooks/useCurrency';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { media } from 'assets/css/media';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import styled, { css } from 'styled-components';
import { useSwipeCoordinates } from 'hooks/useSwipeCoordinates';
import { Button } from 'reactstrap';
import { get } from 'lodash';
import { useSliderIndex } from './useSliderIndex';
import { useIndexedImages } from 'hooks/useIndexedImages';
import {
  setCropImageAction,
  setSelectedImagesSuccessAction,
  // setSelectedImagesSuccessAction,
  setSelectedProductIDAction
} from 'modules/ScrShopNew/store/slices/shop';
import { addProductToCartAction } from 'modules/ScrShopNew/store/slices/checkout/actions';
import { useIsCheckoutReady } from 'modules/ScrShopNew/hooks/useIsCheckoutReady';
import { getProductGroupByID } from 'modules/ScrShopNew/store/slices/products/selectors';
import { IProduct, IProductGroup } from 'modules/ScrShopNew/store/slices/products/types';
import { useIsRedirectToPaymentPage } from 'hooks/useIsRedirectToPaymentPage';
import { usePackageMatching } from 'modules/ScrShopNew/hooks/usePackageMatching';
// import { getProductGroupByID } from 'modules/ScrShopNew/store/slices/products/selectors';
// import { IProductGroup } from 'modules/ScrShopNew/store/slices/products/types';

const SwiperContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  .slick-track {
    display: flex;
    align-items: flex-end;
  }

  .slick-slider {
    width: 100%;
  }

  .slick-dots {
    bottom: -10px;
    li {
      margin: 0;
    }
  }

  .slick-slide {
    height: auto;
    padding: 0 5px;
    > div {
      height: 100%;
    }
  }
`;

const arrowStyles = css`
  cursor: pointer;
  position: absolute;
  width: 25px;
  height: 25px;
  color: #000;

  // A set chevron colours through currentColor; a CSS fill turns feather's outline into a
  // solid triangle. Only the react-icons fallback needs it.
  &:not([data-icon-set]) {
    fill: #000;
  }
`;

const ChevronLeftSetIcon = (props: IIconProps) => (
  <SetIcon slug="chevron" fallback={FaAngleLeft} {...props} />
);

const ChevronRightSetIcon = (props: IIconProps) => (
  <SetIcon slug="chevron" fallback={FaAngleRight} rotate={180} {...props} />
);

const LeftArrow = styled(ChevronLeftSetIcon)`
  ${arrowStyles}
  left: -25px;
`;
const RightArrow = styled(ChevronRightSetIcon)`
  ${arrowStyles}
  right: -25px;
`;

const PriceContent = styled.div`
  margin-top: 40px;
  @media all and (max-width: ${media.desktopLg}) {
    margin-top: 25px;
  }
`;

const EconomyContent = styled.div`
  margin: 20px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f7eef;
  font-size: 14px;
  font-weight: bold;
`;

const SparkledIcon = styled(HiSparkles)`
  margin-right: 5px;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding-bottom: 20px;
  margin-top: 30px;
  @media all and (max-width: ${media.desktopLg}) {
    margin-top: 0;
  }
`;

const StyledButton = styled(Button)`
  && {
    width: calc(50% - 20px);
    border-radius: 40px !important;
    padding: 6px 12px !important;
    height: 40px !important;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 15px;
    font-weight: bold;

    svg {
      margin-right: 10px;
      width: 22px;
      height: 22px;
      fill: none !important;
    }

    &:first-child {
      background: #fff;
      color: #707070;

      @media all and (max-width: ${media.desktopLg}) {
        margin-bottom: 15px;
      }
    }

    @media all and (max-width: ${media.desktopLg}) {
      width: 100%;
    }

    &:disabled {
      cursor: no-drop !important;
      pointer-events: all;
    }
  }
`;

// This button used to inherit its colour: `.sb-btn` paints from the collection palette, and inside
// the portalled modal Bootstrap's `.btn-secondary` grey showed through instead. It is black in every
// template and every state now — disabled only keeps Bootstrap's opacity so it still reads as
// unavailable.
const CartButton = styled(StyledButton)`
  &&,
  &&:hover,
  &&:focus,
  &&:active,
  &&:disabled {
    background-color: #000 !important;
    border-color: #000 !important;
    color: #ffffff !important;
    box-shadow: none !important;

    svg,
    svg path {
      color: #ffffff !important;
      stroke: #ffffff !important;
    }
  }

  && {
    cursor: pointer;
  }
`;

interface IProps {
  selection: ISelection;
  toggleSaveSelectionModal: () => void;
}

export const UpsellingSection: React.FC<IProps> = ({ toggleSaveSelectionModal, selection }) => {
  const selectedImagesAmount = useAppSelector(selectActiveLikedImages).length;
  const digitalGroup = useAppSelector(getProductGroupByID)('digital') as IProductGroup;
  const upsellingPackages = digitalGroup?.products;

  const { filteredImagesArr: images } = useIndexedImages({});
  const { likes } = useAppSelector(selectActiveSelectionState);
  const { swipeCoordinates, startSwiping, stopSwiping } = useSwipeCoordinates();
  // const { isShareSite } = useAppSelector((state) => state.collection);
  const isDesktop = useBreakpoint('desktopXl');
  const sliderRef = useRef<any>(null);
  const currency = useCurrency();

  const dispatch = useAppDispatch();
  const history = useHistory();

  const [selectedPackage, setSelectedPackage] = useState<null | IProduct>(null);
  const [isReadyForRedirectToBasket, setIsReadyForRedirectToBasket] = useState(false);
  const isCheckoutReady = useIsCheckoutReady();
  const isRedirectToPaymentPage = useIsRedirectToPaymentPage();

  useEffect(() => {
    dispatch(getEndCustomer());
  }, []);

  useEffect(() => {
    if (isCheckoutReady && isReadyForRedirectToBasket) {
      // close modal before redirecting
      dispatch(toggleUpsellModal());

      history.push('/shop-new/basket');
    }
  }, [isReadyForRedirectToBasket, isCheckoutReady]);

  const {
    matchedPackage,
    alreadyPurchasedPackage,
    packagesSortedByTier,
    packagesSortedByTierWithInitialPrice,
    allImagesPackage
  } = usePackageMatching({
    productGroup: digitalGroup,
    selectedPackage,
    currentImagesAmount: selectedImagesAmount
  });

  const addImagesToCart = async (productId: string) => {
    if (isRedirectToPaymentPage) {
      dispatch(toggleUpsellModal());
      history.push('/shop-new/payment');

      return;
    }

    dispatch(setSelectedImagesSuccessAction(likes));

    likes.forEach((favorite: any) => {
      dispatch(setCropImageAction({ imageID: favorite, cropData: null }));
    });

    dispatch(setSelectedProductIDAction(productId));
    dispatch(
      addProductToCartAction({
        selectionId: selection._id
      })
    );

    setIsReadyForRedirectToBasket(true);
  };

  const addImagesToCartWithSwipeHandler = (productId: string) => {
    if (swipeCoordinates.start !== swipeCoordinates.end) return;
    addImagesToCart(productId);
  };

  const toggleSaveSelection = () => {
    if (swipeCoordinates.start !== swipeCoordinates.end) return;
    toggleSaveSelectionModal();
  };

  const allImagesPackages = allImagesPackage ? [allImagesPackage] : [];

  const sortedPackages = [...upsellingPackages].sort((a, b) => {
    if (a.imagesIncluded === null && b.imagesIncluded === null) return 0;
    if (a.imagesIncluded === null) return 1;
    if (b.imagesIncluded === null) return -1;

    return a.imagesIncluded - b.imagesIncluded;
  });

  const lowestTierPackage = [...upsellingPackages].sort((a, b) => {
    if (a.imagesIncluded !== null && b.imagesIncluded !== null) {
      return a.imagesIncluded - b.imagesIncluded;
    }

    return 0;
  })[0];

  const matchedPackageWithInitialPrice = packagesSortedByTierWithInitialPrice.find(
    (pckg) => pckg._id === matchedPackage?._id
  );

  const sameAmountPackages = packagesSortedByTier.filter(
    ({ imagesIncluded }) => imagesIncluded === matchedPackage?.imagesIncluded
  );

  const matchedPackageIdx = sortedPackages.findIndex((item) => item._id === matchedPackage?._id);
  const nextTierPackage = sortedPackages[matchedPackageIdx + 1];
  const nextTierImageQuantity = get(nextTierPackage, 'imagesIncluded');
  const imagesForNextTier = nextTierImageQuantity
    ? nextTierImageQuantity - selectedImagesAmount
    : images.length - selectedImagesAmount;

  const notEnoughImagesSelected = selectedImagesAmount < packagesSortedByTier[0]?.imagesIncluded;

  const getSavings = (pckg: IProduct) => {
    const quantity = get(pckg, 'imagesIncluded') || images.length;
    const costPerImage = get(pckg, 'priceTotal') / quantity;

    const lowestTierQuantity = get(lowestTierPackage, 'imagesIncluded') || images.length;
    const lowestTierCostPerImage = get(lowestTierPackage, 'priceTotal') / lowestTierQuantity;

    if (
      lowestTierPackage &&
      lowestTierPackage?._id !== pckg?._id &&
      lowestTierCostPerImage > costPerImage
    ) {
      return Math.round(100 * (1 - costPerImage / lowestTierCostPerImage));
    }

    return null;
  };

  const selectPackage = (packageItem: IProduct) => setSelectedPackage(packageItem);

  const otherPackagesCards = packagesSortedByTier.map((pckg) => (
    <UpsellingPackageCard
      imagePackage={pckg}
      key={pckg._id}
      selectedImagesAmount={selectedImagesAmount}
      addPackageToCart={addImagesToCartWithSwipeHandler}
      isAlreadyPurchasedPackage={
        !!alreadyPurchasedPackage && pckg._id === alreadyPurchasedPackage._id
      }
      matchedPackage={matchedPackage}
      toggleSaveSelectionModal={toggleSaveSelection}
      packagesSortedByTier={packagesSortedByTier}
      sameAmountPackages={sameAmountPackages}
      selectPackage={selectPackage}
      savings={getSavings(pckg)}
      bestseller={pckg?.bestseller}
    />
  ));

  const allImagesPackagesCards = allImagesPackages?.map((pckg) => (
    <UpsellingAllImagesCard
      key={pckg._id}
      imagePackage={pckg}
      isSwiping={swipeCoordinates.start !== swipeCoordinates.end}
      savings={getSavings(pckg)}
      matchedPackage={matchedPackage}
      bestseller={pckg?.bestseller}
      images={images}
      selectionId={selection._id || undefined}
    />
  ));

  const allPackagesCards = [...otherPackagesCards, ...allImagesPackagesCards];

  const priceBreakDownRows: Array<[any, number, number]> = [];

  if (matchedPackage) {
    priceBreakDownRows.push([
      matchedPackage.name || matchedPackage.title,
      matchedPackage.imagesIncluded || selectedImagesAmount,
      matchedPackageWithInitialPrice?.priceTotal || matchedPackage.priceTotal
    ]);

    const currentQuantity = selectedImagesAmount - (matchedPackage.imagesIncluded || 0);

    if (currentQuantity > 0 && !matchedPackage.allImagesIncluded) {
      priceBreakDownRows.push([
        getTranslationKey('shop.upselling.additionalData'),
        currentQuantity,
        currentQuantity * (matchedPackage.additionalImagePriceTotal || 0)
      ]);
    }
  }

  if (alreadyPurchasedPackage) {
    priceBreakDownRows.push([
      getTranslationKey('shop.upselling.alreadyPurchased'),
      -alreadyPurchasedPackage.imagesIncluded,
      -alreadyPurchasedPackage.priceTotal
    ]);
  }

  const { sliderIndex, setSliderIndex } = useSliderIndex({
    matchedPackage,
    packages: [...packagesSortedByTier, ...allImagesPackages],
    slickGoTo: (index: number) => sliderRef?.current?.slickGoTo(index)
  });

  const slidesToShow = isDesktop ? 3 : 1;
  const totalSlides = allPackagesCards.length - (slidesToShow - 1);

  const isFirstSlide = sliderIndex === 0 || allPackagesCards.length <= 3;
  const isLastSlide = sliderIndex === totalSlides - 1 || allPackagesCards.length <= 3;

  const slideLeftArrow = () => sliderRef?.current?.slickPrev();
  const slideRightArrow = () => sliderRef?.current?.slickNext();

  const showLowerPart = !!priceBreakDownRows.length || !matchedPackage;

  return (
    <div className="w-100 px-3 px-lg-4">
      <SwiperContainer
        onMouseDown={startSwiping}
        onMouseUp={stopSwiping}
        onMouseLeave={stopSwiping}
        onTouchStart={startSwiping}
        onTouchEnd={stopSwiping}
      >
        {!isFirstSlide && <LeftArrow onClick={slideLeftArrow} />}
        <Slider
          ref={sliderRef}
          speed={300}
          slidesToScroll={1}
          slidesToShow={slidesToShow}
          initialSlide={sliderIndex}
          useTransform={false}
          arrows={false}
          infinite={false}
          dots={!isDesktop}
          beforeChange={(_: number, newIndex: number) => setSliderIndex(newIndex)}
        >
          {allPackagesCards.sort((a, b) => a.props.imagePackage.tier - b.props.imagePackage.tier)}
        </Slider>
        {!isLastSlide && <RightArrow onClick={slideRightArrow} />}
      </SwiperContainer>

      {showLowerPart && (
        <PriceContent>
          <PriceBreakdown
            currency={currency}
            rows={priceBreakDownRows}
            showFilesOnly={!matchedPackage}
          />
        </PriceContent>
      )}

      {!notEnoughImagesSelected && imagesForNextTier > 0 && getSavings(nextTierPackage) && (
        <EconomyContent>
          <SparkledIcon />
          {getTranslationKey('shop.upselling.selectMoreImages', {
            files: imagesForNextTier,
            savings: String(getSavings(nextTierPackage))
          })}
        </EconomyContent>
      )}

      {notEnoughImagesSelected && (
        <EconomyContent>
          <SparkledIcon />
          {getTranslationKey('shop.upselling.addMoreImages', {
            files: packagesSortedByTier[0]?.imagesIncluded
          })}
        </EconomyContent>
      )}

      {showLowerPart && (
        <ButtonSection>
          <StyledButton onClick={toggleSaveSelectionModal}>
            <SetIcon slug="edit" fallback={TbEdit} />
            <span>{getTranslationKey('shop.upselling.continueSelection')}</span>
          </StyledButton>
          <CartButton
            disabled={!matchedPackage}
            onClick={() => addImagesToCart(matchedPackage!._id)}
          >
            <SetIcon slug="cart" fallback={FiShoppingBag} />
            <span>{getTranslationKey('shop.upselling.goToCart')}</span>
          </CartButton>
        </ButtonSection>
      )}
    </div>
  );
};
